'use client';

import { useState, useRef } from 'react';
import HeroSection from './HeroSection';
import ProgressTracker from './ProgressTracker';
import ModuleContent from './ModuleContent';
import InteractiveQuiz from './InteractiveQuiz';
import ComparisonTool from './ComparisonTool';
import DownloadableResources from './DownloadableResources';
import RegionalExamples from './RegionalExamples';
import ExpertInsight from './ExpertInsight';
import WhatsAppHelper from './WhatsAppHelper';
import Icon from '@/components/ui/AppIcon';

interface Module {
  id: number;
  title: string;
  completed: boolean;
}

interface ContentSection {
  type: 'text' | 'image' | 'list' | 'highlight';
  content: string | string[];
  alt?: string;
}

interface ModuleData {
  title: string;
  description: string;
  sections: ContentSection[];
  icon: string;
}

interface QuizQuestion {
  id: number;
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
}

interface CreditType {
  id: string;
  name: string;
  description: string;
  price: string;
  verification: string;
  timeline: string;
  benefits: string[];
  icon: string;
}

interface Resource {
  id: number;
  title: string;
  description: string;
  language: string;
  format: string;
  size: string;
  icon: string;
}

interface RegionalExample {
  id: number;
  country: string;
  region: string;
  project: string;
  description: string;
  impact: string;
  image: string;
  alt: string;
  flag: string;
}

const CarbonCreditsInteractive = () => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [currentModule, setCurrentModule] = useState(1);
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [finalScore, setFinalScore] = useState(0);

  const modules: Module[] = [
  { id: 1, title: "What Are Carbon Credits?", completed: false },
  { id: 2, title: "How Carbon Markets Work", completed: false },
  { id: 3, title: "Verification & Standards", completed: false },
  { id: 4, title: "Getting Started", completed: false }];


  const [moduleProgress, setModuleProgress] = useState(modules);

  const moduleData: Record<number, ModuleData> = {
    1: {
      title: "What Are Carbon Credits?",
      description: "Understanding the fundamentals of carbon credits and their role in climate action",
      icon: "AcademicCapIcon",
      sections: [
      {
        type: "text",
        content: "A carbon credit represents one metric ton of carbon dioxide (CO₂) or its equivalent in other greenhouse gases that has been reduced, avoided, or removed from the atmosphere. Think of it as a certificate that proves environmental impact."
      },
      {
        type: "image",
        content: "https://images.unsplash.com/photo-1663583128901-faaa4af78cc3",
        alt: "Dense green forest with tall trees and sunlight filtering through canopy showing natural carbon sequestration"
      },
      {
        type: "highlight",
        content: "Key Insight: Carbon credits create economic value for environmental protection, making sustainability financially viable for businesses and communities."
      },
      {
        type: "list",
        content: [
        "One credit = One ton of CO₂ reduced or removed",
        "Verified by international standards (Gold Standard, Verra)",
        "Can be traded in voluntary or compliance markets",
        "Supports renewable energy, reforestation, and conservation projects"]

      },
      {
        type: "text",
        content: "Carbon credits work through a simple principle: activities that reduce greenhouse gas emissions generate credits, which can then be purchased by organizations looking to offset their carbon footprint. This creates a financial incentive for environmental protection."
      }]

    },
    2: {
      title: "How Carbon Markets Work",
      description: "Exploring the mechanisms of voluntary and compliance carbon markets",
      icon: "ChartBarIcon",
      sections: [
      {
        type: "text",
        content: "Carbon markets operate on two main levels: compliance markets (regulated by governments) and voluntary markets (driven by corporate sustainability goals). Understanding both is crucial for maximizing opportunities."
      },
      {
        type: "list",
        content: [
        "Compliance Markets: Mandatory participation under regulations like EU ETS",
        "Voluntary Markets: Companies voluntarily purchase credits for CSR goals",
        "Price Discovery: Market forces determine credit values based on quality and demand",
        "Trading Platforms: Credits are bought and sold through specialized exchanges"]

      },
      {
        type: "image",
        content: "https://img.rocket.new/generatedImages/rocket_gen_img_13913910e-1764639852428.png",
        alt: "Modern office workspace with multiple computer screens displaying financial charts and data analytics"
      },
      {
        type: "highlight",
        content: "Market Opportunity: The voluntary carbon market is projected to reach $50 billion by 2030, creating significant opportunities for project developers."
      },
      {
        type: "text",
        content: "In India and UAE, both compliance and voluntary markets are growing rapidly. India's carbon market is expanding through renewable energy projects, while UAE focuses on clean energy and carbon capture initiatives aligned with their net-zero commitments."
      }]

    },
    3: {
      title: "Verification & Standards",
      description: "Understanding the certification process and quality standards",
      icon: "ShieldCheckIcon",
      sections: [
      {
        type: "text",
        content: "Verification ensures that carbon credits represent real, measurable, and permanent emission reductions. This process is critical for market integrity and buyer confidence."
      },
      {
        type: "list",
        content: [
        "Third-party verification by accredited auditors",
        "Adherence to international standards (Verra VCS, Gold Standard, CDM)",
        "Regular monitoring and reporting requirements",
        "Permanence guarantees for long-term impact"]

      },
      {
        type: "image",
        content: "https://img.rocket.new/generatedImages/rocket_gen_img_1325dcaa7-1764671154592.png",
        alt: "Close-up of official certification documents with stamps and signatures on wooden desk"
      },
      {
        type: "highlight",
        content: "Quality Matters: High-quality verified credits command premium prices and provide stronger environmental and social co-benefits."
      },
      {
        type: "text",
        content: "The verification process typically takes 6-12 months and involves baseline assessment, project implementation monitoring, third-party auditing, and final certification. GenCarbon guides clients through every step of this journey."
      }]

    },
    4: {
      title: "Getting Started",
      description: "Practical steps to begin your carbon credit journey",
      icon: "RocketLaunchIcon",
      sections: [
      {
        type: "text",
        content: "Starting your carbon credit journey requires understanding your baseline emissions, identifying reduction opportunities, and choosing the right project type for your context."
      },
      {
        type: "list",
        content: [
        "Conduct a carbon footprint assessment",
        "Identify eligible projects (renewable energy, reforestation, efficiency)",
        "Choose appropriate standards and methodologies",
        "Partner with experienced consultants like GenCarbon",
        "Develop project documentation and monitoring plans"]

      },
      {
        type: "image",
        content: "https://img.rocket.new/generatedImages/rocket_gen_img_1478bcc45-1764651525877.png",
        alt: "Business team collaborating around table with laptops and documents in bright modern office"
      },
      {
        type: "highlight",
        content: "Expert Support: GenCarbon provides end-to-end support from project design through verification and credit trading."
      },
      {
        type: "text",
        content: "Whether you're a small business in India or a large enterprise in UAE, there are carbon credit opportunities suited to your scale and sector. The key is starting with a clear strategy and expert guidance."
      }]

    }
  };

  const quizQuestions: QuizQuestion[] = [
  {
    id: 1,
    question: "What does one carbon credit represent?",
    options: [
    "One kilogram of CO₂ reduced",
    "One metric ton of CO₂ reduced or removed",
    "One year of carbon neutrality",
    "One tree planted"],

    correctAnswer: 1,
    explanation: "One carbon credit represents one metric ton (1,000 kg) of carbon dioxide or its equivalent in other greenhouse gases that has been reduced, avoided, or removed from the atmosphere."
  },
  {
    id: 2,
    question: "Which of these is NOT a recognized carbon credit standard?",
    options: [
    "Gold Standard",
    "Verra VCS",
    "Silver Certificate",
    "Clean Development Mechanism (CDM)"],

    correctAnswer: 2,
    explanation: "Gold Standard, Verra VCS, and CDM are all recognized international carbon credit standards. 'Silver Certificate' is not a real carbon credit standard."
  },
  {
    id: 3,
    question: "What is the main difference between compliance and voluntary carbon markets?",
    options: [
    "Compliance markets are cheaper",
    "Voluntary markets are regulated by government",
    "Compliance markets are mandatory under regulations",
    "Voluntary markets only exist in developing countries"],

    correctAnswer: 2,
    explanation: "Compliance markets are mandatory and regulated by governments (like EU ETS), while voluntary markets are driven by companies voluntarily purchasing credits for sustainability goals."
  }];


  const creditTypes: CreditType[] = [
  {
    id: "renewable",
    name: "Renewable Energy",
    description: "Credits from solar, wind, and hydro projects",
    price: "$5-15 per ton",
    verification: "Verra VCS, Gold Standard",
    timeline: "6-12 months",
    benefits: ["Clean energy generation", "Grid decarbonization", "Energy independence"],
    icon: "BoltIcon"
  },
  {
    id: "forestry",
    name: "Forestry & REDD+",
    description: "Forest conservation and reforestation projects",
    price: "$8-20 per ton",
    verification: "Verra VCS, Gold Standard",
    timeline: "12-18 months",
    benefits: ["Biodiversity protection", "Community livelihoods", "Long-term sequestration"],
    icon: "HomeModernIcon"
  },
  {
    id: "efficiency",
    name: "Energy Efficiency",
    description: "Industrial and building efficiency improvements",
    price: "$4-12 per ton",
    verification: "CDM, Gold Standard",
    timeline: "6-9 months",
    benefits: ["Cost savings", "Reduced energy consumption", "Quick implementation"],
    icon: "CpuChipIcon"
  },
  {
    id: "agriculture",
    name: "Agriculture & Soil",
    description: "Sustainable farming and soil carbon sequestration",
    price: "$10-25 per ton",
    verification: "Verra VCS, Gold Standard",
    timeline: "12-24 months",
    benefits: ["Improved soil health", "Farmer income", "Food security"],
    icon: "SparklesIcon"
  }];


  const resources: Resource[] = [
  {
    id: 1,
    title: "Carbon Credits 101 Guide",
    description: "Comprehensive introduction to carbon markets",
    language: "English",
    format: "PDF",
    size: "2.5 MB",
    icon: "DocumentTextIcon"
  },
  {
    id: 2,
    title: "कार्बन क्रेडिट मार्गदर्शिका",
    description: "Hindi language guide for Indian market",
    language: "Hindi",
    format: "PDF",
    size: "2.8 MB",
    icon: "DocumentTextIcon"
  },
  {
    id: 3,
    title: "دليل ائتمانات الكربون",
    description: "Arabic guide for UAE market",
    language: "Arabic",
    format: "PDF",
    size: "2.6 MB",
    icon: "DocumentTextIcon"
  },
  {
    id: 4,
    title: "Carbon Sequestration Infographic",
    description: "Visual guide to carbon storage processes",
    language: "English",
    format: "PNG",
    size: "1.2 MB",
    icon: "PhotoIcon"
  },
  {
    id: 5,
    title: "Project Development Checklist",
    description: "Step-by-step project planning tool",
    language: "English",
    format: "PDF",
    size: "800 KB",
    icon: "ClipboardDocumentCheckIcon"
  },
  {
    id: 6,
    title: "Verification Process Flowchart",
    description: "Visual guide to certification steps",
    language: "English",
    format: "PDF",
    size: "1.5 MB",
    icon: "ChartBarIcon"
  }];


  const regionalExamples: RegionalExample[] = [
  {
    id: 1,
    country: "India",
    region: "Maharashtra",
    project: "Solar Farm Carbon Credits",
    description: "A 50 MW solar farm in rural Maharashtra generating clean energy and carbon credits, providing electricity to 30,000 homes while creating local employment opportunities.",
    impact: "75,000 tons CO₂ offset annually",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_11184ab41-1765094971396.png",
    alt: "Large-scale solar panel farm with rows of blue photovoltaic panels under bright sunny sky in rural landscape",
    flag: "🇮🇳"
  },
  {
    id: 2,
    country: "UAE",
    region: "Abu Dhabi",
    project: "Mangrove Restoration Initiative",
    description: "Coastal mangrove restoration project protecting 500 hectares of critical marine ecosystem while sequestering carbon and supporting biodiversity.",
    impact: "25,000 tons CO₂ sequestered over 10 years",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_14445bf2c-1765205509478.png",
    alt: "Dense mangrove forest with green vegetation and roots visible in shallow coastal waters at sunset",
    flag: "🇦🇪"
  },
  {
    id: 3,
    country: "India",
    region: "Karnataka",
    project: "Agroforestry Carbon Project",
    description: "Sustainable agroforestry initiative supporting 2,000 farmers with tree planting on agricultural land, improving soil health and generating carbon credits.",
    impact: "40,000 tons CO₂ sequestered over 20 years",
    image: "https://images.unsplash.com/photo-1679548855059-5cf26a0c060f",
    alt: "Farmers working in agricultural field with mixed crops and trees in background under clear blue sky",
    flag: "🇮🇳"
  },
  {
    id: 4,
    country: "UAE",
    region: "Dubai",
    project: "Building Energy Efficiency Program",
    description: "Retrofitting 50 commercial buildings with energy-efficient systems, reducing cooling loads and electricity consumption in Dubai's hot climate.",
    impact: "30,000 tons CO₂ reduced annually",
    image: "https://images.unsplash.com/photo-1700374610029-6f09b715fc80",
    alt: "Modern glass and steel commercial buildings in Dubai skyline with reflective facades under bright sunlight",
    flag: "🇦🇪"
  }];


  const handleScrollToContent = () => {
    contentRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleModuleComplete = () => {
    const updatedModules = moduleProgress.map((m) =>
    m.id === currentModule ? { ...m, completed: true } : m
    );
    setModuleProgress(updatedModules);

    if (currentModule < modules.length) {
      setCurrentModule(currentModule + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      setShowQuiz(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleQuizComplete = (score: number) => {
    setFinalScore(score);
    setQuizCompleted(true);
  };

  const currentModuleData = moduleData[currentModule];

  return (
    <div className="min-h-screen bg-background">
      <HeroSection onScrollToContent={handleScrollToContent} />

      <div ref={contentRef} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        {!showQuiz ?
        <div className="grid lg:grid-cols-12 gap-8">
            <div className="lg:col-span-8 space-y-8">
              <ModuleContent
              title={currentModuleData.title}
              description={currentModuleData.description}
              sections={currentModuleData.sections}
              icon={currentModuleData.icon} />


              <ExpertInsight
              quote="Carbon credits are not just about compliance—they're about creating a sustainable future where environmental protection is economically viable and socially beneficial."
              context="Dr. Nupur emphasizes that understanding carbon credits is the first step toward meaningful climate action. Through proper education and implementation, businesses of all sizes can contribute to global sustainability goals while achieving their own environmental targets." />


              <div className="flex justify-between items-center pt-6 border-t border-border">
                <button
                onClick={() => setCurrentModule(Math.max(1, currentModule - 1))}
                disabled={currentModule === 1}
                className={`inline-flex items-center space-x-2 px-6 py-3 rounded-lg font-heading font-semibold transition-all duration-300 ${
                currentModule === 1 ?
                'bg-muted text-muted-foreground cursor-not-allowed' :
                'bg-surface text-text-primary border border-border hover:bg-primary/10 hover:border-primary/30'}`
                }>

                  <Icon name="ArrowLeftIcon" size={20} />
                  <span>Previous</span>
                </button>

                <button
                onClick={handleModuleComplete}
                className="inline-flex items-center space-x-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg font-heading font-semibold hover:bg-primary/90 shadow-natural hover:shadow-natural-lg transition-all duration-300 ease-organic">

                  <span>{currentModule === modules.length ? 'Take Quiz' : 'Next Module'}</span>
                  <Icon name="ArrowRightIcon" size={20} />
                </button>
              </div>
            </div>

            <div className="lg:col-span-4">
              <ProgressTracker modules={moduleProgress} currentModule={currentModule} />
            </div>
          </div> :
        !quizCompleted ?
        <div className="max-w-3xl mx-auto">
            <InteractiveQuiz questions={quizQuestions} onComplete={handleQuizComplete} />
          </div> :

        <div className="max-w-4xl mx-auto space-y-8">
            <div className="bg-card border border-border rounded-xl p-8 text-center shadow-natural">
              <div className="w-20 h-20 bg-conversion/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Icon name="TrophyIcon" size={40} className="text-conversion" />
              </div>
              <h2 className="font-heading font-bold text-3xl text-text-primary mb-4">
                Congratulations!
              </h2>
              <p className="text-xl text-text-secondary mb-2">
                You scored {finalScore} out of {quizQuestions.length}
              </p>
              <p className="text-text-secondary mb-8">
                You've completed the Carbon Credits 101 course
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                href="/get-started"
                className="inline-flex items-center justify-center space-x-2 px-8 py-4 bg-conversion text-conversion-foreground font-heading font-semibold rounded-lg hover:bg-conversion/90 shadow-natural-lg hover:shadow-natural-xl transition-all duration-300 ease-organic">

                  <span>Start Your Project</span>
                  <Icon name="ArrowRightIcon" size={20} />
                </a>
                <button
                onClick={() => {
                  setShowQuiz(false);
                  setQuizCompleted(false);
                  setCurrentModule(1);
                  setModuleProgress(modules);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="inline-flex items-center justify-center space-x-2 px-8 py-4 bg-surface text-text-primary border border-border font-heading font-semibold rounded-lg hover:bg-primary/10 hover:border-primary/30 transition-all duration-300">

                  <Icon name="ArrowPathIcon" size={20} />
                  <span>Restart Course</span>
                </button>
              </div>
            </div>
          </div>
        }

        <div className="mt-16 space-y-12">
          <ComparisonTool creditTypes={creditTypes} />
          <RegionalExamples examples={regionalExamples} />
          <DownloadableResources resources={resources} />
        </div>

        <div className="mt-16 bg-gradient-to-br from-primary via-primary/95 to-secondary rounded-2xl p-8 lg:p-12 text-center shadow-natural-xl">
          <h2 className="font-heading font-bold text-3xl lg:text-4xl text-primary-foreground mb-4">
            Ready to Generate Carbon Credits?
          </h2>
          <p className="text-lg text-primary-foreground/90 mb-8 max-w-2xl mx-auto">
            Let GenCarbon guide you through the entire process—from project design to credit trading. Schedule a consultation with Dr. Nupur today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="mailto:dr.nupur@gencarbon.in"
              className="inline-flex items-center justify-center space-x-2 px-8 py-4 bg-accent text-accent-foreground font-heading font-semibold rounded-lg hover:bg-accent/90 shadow-natural-lg hover:shadow-natural-xl transition-all duration-300 ease-organic">

              <Icon name="EnvelopeIcon" size={20} />
              <span>Email Dr. Nupur</span>
            </a>
            <a
              href="https://wa.me/1234567890?text=I%20want%20to%20start%20a%20carbon%20credit%20project"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center space-x-2 px-8 py-4 bg-primary-foreground text-primary font-heading font-semibold rounded-lg hover:bg-primary-foreground/90 shadow-natural hover:shadow-natural-lg transition-all duration-300 ease-organic">

              <Icon name="ChatBubbleLeftRightIcon" size={20} />
              <span>WhatsApp Consultation</span>
            </a>
          </div>
        </div>
      </div>

      <WhatsAppHelper />
    </div>);

};

export default CarbonCreditsInteractive;